import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Tag, Space, Typography, Divider, Timeline,
  Button, Avatar, Badge, Empty, Modal, Form, Input, InputNumber, Select, Popconfirm,
  message, Tooltip, Spin
} from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined,
  ShoppingCartOutlined, DollarOutlined, ShopOutlined, CarOutlined, CheckCircleOutlined,
  ClockCircleOutlined, CloseCircleOutlined, EditOutlined, PrinterOutlined,
  FileTextOutlined, ReloadOutlined, SendOutlined, LinkOutlined, PlusOutlined, DeleteOutlined,
  InboxOutlined, RollbackOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig, driversListUrl } from '../../services/ApiConfigs';
import { updatePayment } from '../../services/orderService';
import { createShipment, getShipments, assignDriverToShipment, updateShipmentStatus, deleteShipment } from '../../services/shipmentService';
import { openMap } from '../../services/helper';
import { getStatusConfig } from '../../utils/orderStatusUtils';
import MainLayout from '../../components/MainLayout';
import styles from './OrderDetails.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ASSIGNMENT_MODE_KEY = 'Shipment Assignment Mode';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipmentStatusModalVisible, setShipmentStatusModalVisible] = useState(false);
  const [selectedShipmentForStatus, setSelectedShipmentForStatus] = useState(null);
  const [warehouseModalVisible, setWarehouseModalVisible] = useState(false);
  const [driverModalVisible, setDriverModalVisible] = useState(false);
  const [createShipmentModalVisible, setCreateShipmentModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('admin_assign'); // 'admin_assign' | 'driver_accept'
  const [shipmentStatusForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [warehouseForm] = Form.useForm();
  const [driverForm] = Form.useForm();
  const [createShipmentForm] = Form.useForm();

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  // Available qty per order item (order qty - already shipped in previous shipments)
  const getAvailableQtyByOrderItem = () => {
    const shipped = {};
    for (const s of order?.shipments || []) {
      for (const si of s.shipmentItems || s.ShipmentItems || []) {
        const oiId = si.orderItemId ?? si.order_item_id ?? si.orderItem?.id;
        if (oiId) shipped[oiId] = (shipped[oiId] || 0) + (si.quantity || 0);
      }
    }
    return shipped;
  };

  useEffect(() => {
    if (createShipmentModalVisible && order) {
      const firstShipment = (order.shipments || [])[0];
      const defaultWarehouseId = firstShipment?.pickupWarehouseId ?? firstShipment?.pickupWarehouse?.id ?? null;
      const defaultDriverId = firstShipment?.driverId ?? firstShipment?.driver?.id ?? null;

      const oiList = order.orderItems || order.OrderItems || [];
      const shipped = getAvailableQtyByOrderItem();
      const values = {
        warehouseId: defaultWarehouseId,
        driverId: defaultDriverId,
      };
      if (oiList.length > 0) {
        values.items = oiList.map(oi => {
          const orderQty = oi.quantity || 1;
          const alreadyShipped = shipped[oi.id] || 0;
          const available = Math.max(0, orderQty - alreadyShipped);
          return { orderItemId: oi.id, quantity: available > 0 ? available : 0 };
        });
      }
      createShipmentForm.setFieldsValue(values);
    }
  }, [createShipmentModalVisible, order]);

  const fetchOrderDetails = async () => {
    if (!id) {
      message.error('Order ID is missing');
      navigate('/Orders');
      return;
    }

    setLoading(true);
    try {
      // Fetch single order by ID with related data
      const orderUrl = `${ApiConfig.ORDERS}/${id}`;
      console.log('Fetching order from:', orderUrl);
      
      const response = await ApiServices('get', orderUrl);
      console.log('Order response:', response);
      
      if (response?.success && response?.data) {
        const foundOrder = response.data;
        console.log('Found order:', foundOrder);
        setOrder(foundOrder);
        
        // Fetch warehouses, drivers, and shipment assignment mode
        try {
          const orderStoreId = foundOrder?.store_id;
          const [warehouseRes, driverRes, configRes] = await Promise.all([
            ApiServices('get', ApiConfig.WAREHOUSE),
            ApiServices('get', driversListUrl(orderStoreId)),
            ApiServices('get', ApiConfig.APP_CONFIG),
          ]);
          setWarehouses(warehouseRes?.data || []);
          setDrivers(driverRes?.data || []);
          const configList = configRes?.data?.data || [];
          const modeRow = configList.find((r) => r.Key === ASSIGNMENT_MODE_KEY);
          if (modeRow && (modeRow.value === 'admin_assign' || modeRow.value === 'driver_accept')) {
            setAssignmentMode(modeRow.value);
          }
        } catch (err) {
          console.error('Error fetching warehouses/drivers/config:', err);
        }
      } else {
        const errorMsg = response?.message || 'Order not found';
        console.error('Order not found:', errorMsg, response);
        message.error(errorMsg);
        setTimeout(() => navigate('/Orders'), 2000);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to load order details';
      message.error(errorMsg);
      setTimeout(() => navigate('/Orders'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const getDeliveryAddress = () => {
    const addresses = order?.orderAddresses || order?.OrderAddresses || [];
    return addresses.find(addr => addr.addressType === 'Delivery');
  };

  const handlePaymentUpdate = async (values) => {
    try {
      const payload = {
        paymentStatus: values.paymentStatus,
        paymentReceive: values.paymentReceive,
        paymentType: 'COD',
      };
      const response = await updatePayment(id, payload);
      if (response?.success && response?.data) {
        message.success('Payment updated successfully');
        setPaymentModalVisible(false);
        paymentForm.resetFields();
        await fetchOrderDetails();
      } else {
        message.error(response?.message || 'Failed to update payment');
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to update payment';
      message.error(errMsg);
    }
  };

  const handlePrintChalan = (shipment) => {
    const wh = shipment.pickupWarehouse || warehouses.find(w => w.id === shipment.pickupWarehouseId);
    const deliveryAddr = getDeliveryAddress();
    const items = (shipment.shipmentItems || []).length > 0
      ? (shipment.shipmentItems || []).map(si => {
          const oi = si.orderItem || si.OrderItem;
          return { name: oi?.productName || oi?.Product_name || 'N/A', qty: si.quantity ?? oi?.quantity ?? 1 };
        })
      : (order?.orderItems || order?.OrderItems || []).map(item => ({ name: item.productName || item.Product_name || 'N/A', qty: item.quantity || 1 }));

    const shippingAddr = wh ? [wh.name, wh.address, [wh.city, wh.state, wh.pincode].filter(Boolean).join(', ')].filter(Boolean).join('\n') : 'Not assigned';
    const dropAddr = deliveryAddr ? [deliveryAddr.full_name || order?.customerName, deliveryAddr.line1, deliveryAddr.line2, deliveryAddr.landmark, deliveryAddr.area, deliveryAddr.building_name, [deliveryAddr.city, deliveryAddr.state, deliveryAddr.zipcode].filter(Boolean).join(' '), deliveryAddr.country, deliveryAddr.phone].filter(Boolean).join('\n') : 'Not available';

    const html = `
<!DOCTYPE html>
<html>
<head><title>Chalan - ${shipment.shipmentNumber || 'Shipment'}</title>
<style>
body{font-family:Arial,sans-serif;padding:20px;max-width:600px;margin:0 auto}
h1{font-size:18px;border-bottom:2px solid #333;padding-bottom:8px}
h2{font-size:14px;margin:16px 0 8px;color:#555}
table{width:100%;border-collapse:collapse;margin:8px 0}
th,td{border:1px solid #ddd;padding:8px;text-align:left}
th{background:#f5f5f5}
.address{white-space:pre-line;padding:8px;background:#f9f9f9;border:1px solid #ddd;border-radius:4px}
.meta{color:#666;font-size:12px;margin-bottom:16px}
</style>
</head>
<body>
<h1>CHALAN</h1>
<div class="meta">Order #${order?.orderId || '—'} | ${shipment.shipmentNumber || 'Shipment'} | ${moment().format('DD MMM YYYY, HH:mm')}</div>

<h2>Product Details</h2>
<table><thead><tr><th>#</th><th>Product</th><th>Qty</th></tr></thead>
<tbody>${items.map((it, i) => `<tr><td>${i + 1}</td><td>${it.name}</td><td>${it.qty}</td></tr>`).join('')}</tbody>
</table>

<h2>Pickup Address (Warehouse)</h2>
<div class="address">${shippingAddr}</div>

<h2>Delivery Address</h2>
<div class="address">${dropAddr}</div>

</body>
</html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 250);
  };

  const handleDeleteShipment = async (shipmentId) => {
    try {
      await deleteShipment(shipmentId);
      message.success('Shipment deleted. Order status recalculated.');
      await fetchOrderDetails();
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to delete shipment';
      message.error(errMsg);
    }
  };

  const handleShipmentStatusUpdate = async (values) => {
    if (!selectedShipmentForStatus) return;
    try {
      const response = await updateShipmentStatus(selectedShipmentForStatus.id, {
        shipmentStatus: values.shipmentStatus,
        changedByType: 'admin',
        note: values.note || undefined,
      });
      if (response?.data) {
        message.success('Shipment status updated. Order status recalculated.');
        setShipmentStatusModalVisible(false);
        setSelectedShipmentForStatus(null);
        shipmentStatusForm.resetFields();
        await fetchOrderDetails();
      } else {
        message.error(response?.message || 'Failed to update shipment status');
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to update shipment status';
      message.error(errMsg);
    }
  };

  const handleAssignWarehouse = async (values) => {
    try {
      const shipmentId = selectedShipmentId;
      if (shipmentId) {
        const response = await ApiServices('put', `${ApiConfig.SHIPMENTS}/${shipmentId}`, { pickupWarehouseId: values.warehouseId });
        if (response?.data || response?.message) {
          message.success('Warehouse assigned successfully');
          setWarehouseModalVisible(false);
          setSelectedShipmentId(null);
          warehouseForm.resetFields();
          fetchOrderDetails();
        } else message.error(response?.message || 'Failed');
      } else {
        const response = await createShipment({
          orderId: id,
          storeId: order?.store_id,
          pickupWarehouseId: values.warehouseId,
        });
        if (response?.data || response?.message) {
          message.success('Shipment created with warehouse');
          setWarehouseModalVisible(false);
          warehouseForm.resetFields();
          fetchOrderDetails();
        } else message.error(response?.message || 'Failed');
      }
    } catch (error) {
      message.error(error?.message || 'Failed to assign warehouse');
    }
  };

  const handleAssignDriver = async (values) => {
    try {
      const shipmentId = selectedShipmentId;
      if (shipmentId) {
        const response = await assignDriverToShipment(shipmentId, values.driverId);
        if (response?.data || response?.message) {
          message.success('Driver assigned successfully');
          setDriverModalVisible(false);
          setSelectedShipmentId(null);
          driverForm.resetFields();
          fetchOrderDetails();
        } else message.error(response?.message || 'Failed');
      } else {
        const response = await createShipment({
          orderId: id,
          storeId: order?.store_id,
          driverId: values.driverId || null,
          pickupWarehouseId: (order?.shipments || [])[0]?.pickupWarehouseId || null,
        });
        if (response?.data || response?.message) {
          message.success(values.driverId ? 'Shipment created and driver assigned' : 'Shipment created. A driver can accept from their app.');
          setDriverModalVisible(false);
          driverForm.resetFields();
          fetchOrderDetails();
        } else message.error(response?.message || 'Failed');
      }
    } catch (error) {
      message.error(error?.message || 'Failed to assign driver');
    }
  };

  const handleCreateShipment = async (values) => {
    try {
      const items = (values.items || [])
        .filter(it => it.quantity > 0)
        .map(it => ({ orderItemId: it.orderItemId, quantity: it.quantity }));
      const response = await createShipment({
        orderId: id,
        storeId: order?.store_id,
        driverId: values.driverId || null,
        pickupWarehouseId: values.warehouseId || null,
        ...(items.length > 0 && { items }),
      });
      if (response?.data || response?.message) {
        message.success('Shipment created successfully');
        setCreateShipmentModalVisible(false);
        createShipmentForm.resetFields();
        fetchOrderDetails();
      } else message.error(response?.message || 'Failed');
    } catch (error) {
      message.error(error?.message || 'Failed to create shipment');
    }
  };

  const handleArchiveOrder = async () => {
    try {
      const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${id}/archive`);
      if (res?.success || res?.message === 'Order archived successfully') {
        message.success('Order archived');
        fetchOrderDetails();
      } else {
        message.error(res?.message || 'Failed to archive order');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to archive order');
    }
  };

  const handleUnarchiveOrder = async () => {
    try {
      const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${id}/unarchive`);
      if (res?.success || res?.message === 'Order restored from archive') {
        message.success('Order restored from archive');
        fetchOrderDetails();
      } else {
        message.error(res?.message || 'Failed to restore order');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to restore order');
    }
  };

  const handleDeleteOrder = () => {
    Modal.confirm({
      title: 'Delete order permanently?',
      content: (
        <>
          Order <strong>{order?.orderId}</strong> and all related data (shipments, items, addresses) will be deleted. This cannot be undone.
        </>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await ApiServices('delete', `${ApiConfig.ORDERS_UPDATE_STATUS}/${id}`);
          if (res?.success || res?.message?.toLowerCase().includes('deleted')) {
            message.success('Order deleted');
            navigate('/Orders');
          } else {
            message.error(res?.message || 'Failed to delete order');
          }
        } catch (err) {
          message.error(err?.message || 'Failed to delete order');
        }
      },
    });
  };

  const TopBar = () => (
    <div className={styles.topBar}>
      <div className={styles.topBarLeft}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/Orders')}
          className={styles.topBarBack}
        >
          Back to Orders
        </Button>
      </div>
      {order && (
        <div className={styles.topBarRight}>
          {order.customerPhone && (
            <Tooltip title={`Call ${order.customerName}`}>
              <Button
                size="small"
                type="primary"
                className={styles.actionBtnPrimary}
                icon={<PhoneOutlined />}
                onClick={() => window.open(`tel:${order.customerPhone}`)}
              >
                Call Customer
              </Button>
            </Tooltip>
          )}
          <Button size="small" className={styles.actionBtn} icon={<ReloadOutlined />} onClick={fetchOrderDetails}>
            Refresh
          </Button>
          <Button size="small" className={styles.actionBtn} icon={<PlusOutlined />} onClick={() => setCreateShipmentModalVisible(true)}>
            Add Shipment
          </Button>
          {order.archivedAt ? (
            <Button size="small" className={styles.actionBtn} icon={<RollbackOutlined />} onClick={handleUnarchiveOrder}>
              Restore from archive
            </Button>
          ) : (
            <Button size="small" className={styles.actionBtn} icon={<InboxOutlined />} onClick={handleArchiveOrder}>
              Archive order
            </Button>
          )}
          <Button
            size="small"
            danger
            className={styles.actionBtnDanger}
            icon={<DeleteOutlined />}
            onClick={handleDeleteOrder}
          >
            Delete order
          </Button>
          <Button size="small" className={styles.actionBtn} icon={<PrinterOutlined />}>
            Print Label
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <MainLayout>
        <div className={styles.page}>
          <TopBar />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Spin size="large" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <div className={styles.page}>
          <TopBar />
          <Empty description="Order not found" />
        </div>
      </MainLayout>
    );
  }

  const statusConfig = getStatusConfig(order.orderStatus || order.status);
  const deliveryAddress = getDeliveryAddress();
  const orderItems = order.orderItems || order.OrderItems || [];

  return (
    <MainLayout>
      <div className={styles.page}>
        <TopBar />

        {/* Top Summary Card - Premium with status strip */}
        <Card
          className={styles.summaryCard}
          style={{
            marginBottom: 16,
            background: `linear-gradient(135deg, ${statusConfig.bg} 0%, #ffffff 100%)`,
            borderLeft: `4px solid ${statusConfig.stripColor}`,
          }}
          styles={{ body: { padding: '14px 18px' } }}
        >
          <Space direction="vertical" size={4}>
            <Space wrap size={8}>
              <Title level={4} style={{ margin: 0, fontWeight: 600, fontSize: 17 }}>
                Order #{order.orderId}
              </Title>
              <Tag
                style={{
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: 6,
                  backgroundColor: statusConfig.bg,
                  border: `1px solid ${statusConfig.color}`,
                  color: statusConfig.textColor || statusConfig.color,
                }}
                icon={statusConfig.icon}
              >
                {statusConfig.text}
              </Tag>
              {order.paymentType === 'COD' && (
                <Tag className={styles.codBadge} style={{ fontSize: 11 }}>
                  COD {order.currency_symbol || '₹'}{parseFloat(order.amount || 0).toFixed(2)}
                </Tag>
              )}
            </Space>
            <div className={styles.metaTags}>
              {order.platformType && (
                <Tag className={styles.metaTag} color="purple">{String(order.platformType).charAt(0).toUpperCase() + order.platformType.slice(1)}</Tag>
              )}
              {order.shopifyOrderGid && (
                <Tag className={styles.metaTag} color="blue">#{order.shopifyOrderGid.split('/').pop()}</Tag>
              )}
              {order.custom_location_id && (
                <Tag className={styles.metaTag} color="cyan">Location: {order.custom_location_id}</Tag>
              )}
              <Tag className={styles.metaTag}>{order.paymentType || 'N/A'}</Tag>
              <Tag className={styles.metaTag}>{order.createdAt ? moment(order.createdAt).format('DD MMM YYYY') : '—'}</Tag>
            </div>
          </Space>
        </Card>

        <Row gutter={16} style={{ marginBottom: 0 }}>
          {/* Right Column - Actions first on mobile */}
          <Col xs={{ span: 24, order: 1 }} lg={{ span: 10, order: 2 }} className={styles.rightColumn}>
            {deliveryAddress?.latitude && deliveryAddress?.longitude ? (
              <Card
                className={styles.mapCard}
                title={
                  <Space>
                    <EnvironmentOutlined className={styles.sectionIcon} />
                    <span>Delivery Location</span>
                  </Space>
                }
                style={{ marginBottom: 12 }}
                extra={
                  <Button
                    type="link"
                    size="small"
                    icon={<EnvironmentOutlined />}
                    onClick={() => openMap(deliveryAddress.latitude, deliveryAddress.longitude)}
                  >
                    Open in Maps
                  </Button>
                }
              >
                <div className={styles.mapContainer}>
                  <iframe
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${deliveryAddress.latitude},${deliveryAddress.longitude}&hl=en&z=15&output=embed`}
                    title="Delivery Location"
                  />
                </div>
                <div className={styles.locationMeta}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {deliveryAddress.latitude}, {deliveryAddress.longitude}
                    {deliveryAddress.area && ` · ${deliveryAddress.area}`}
                    {deliveryAddress.city && ` · ${deliveryAddress.city}`}
                  </Text>
                </div>
              </Card>
            ) : (
              <Card className={styles.mapCard} style={{ marginBottom: 16 }} title={<Space><EnvironmentOutlined className={styles.sectionIcon} /><span>Delivery Location</span></Space>}>
                <div className={styles.mapPlaceholder}>
                  <EnvironmentOutlined className={styles.mapPlaceholderIcon} />
                  <Text type="secondary">No location data available</Text>
                </div>
              </Card>
            )}

            <Card className={styles.timelineCard} title={<Space><ClockCircleOutlined className={styles.sectionIcon} /><span>Order Flow</span></Space>}>
              <Timeline
                mode="left"
                items={[
                  {
                    color: '#22c55e',
                    dot: <CheckCircleOutlined />,
                    children: (
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Order Created</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{order.createdAt ? moment(order.createdAt).format('DD MMM YYYY, HH:mm') : '—'}</Text></div>
                      </div>
                    ),
                  },
                  {
                    color: (order.shipments || [])[0]?.pickedUpAt ? '#22c55e' : '#d9d9d9',
                    dot: (order.shipments || [])[0]?.pickedUpAt ? <CheckCircleOutlined /> : <ClockCircleOutlined />,
                    children: (
                      <div>
                        <Text strong={!!(order.shipments || [])[0]?.pickedUpAt} style={{ fontSize: 13 }}>Picked Up</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{(order.shipments || [])[0]?.pickedUpAt ? moment((order.shipments || [])[0].pickedUpAt).format('DD MMM YYYY, HH:mm') : '—'}</Text></div>
                      </div>
                    ),
                  },
                  {
                    color: (order.shipments || [])[0]?.outForDeliveryAt ? '#22c55e' : '#d9d9d9',
                    dot: (order.shipments || [])[0]?.outForDeliveryAt ? <CheckCircleOutlined /> : <ClockCircleOutlined />,
                    children: (
                      <div>
                        <Text strong={!!(order.shipments || [])[0]?.outForDeliveryAt} style={{ fontSize: 13 }}>Out for Delivery</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{(order.shipments || [])[0]?.outForDeliveryAt ? moment((order.shipments || [])[0].outForDeliveryAt).format('DD MMM YYYY, HH:mm') : '—'}</Text></div>
                      </div>
                    ),
                  },
                  {
                    color: ['DELIVERED', 'COMPLETED', 'PARTIAL_DELIVERED'].includes(order.orderStatus) ? '#22c55e' : '#d9d9d9',
                    dot: ['DELIVERED', 'COMPLETED', 'PARTIAL_DELIVERED'].includes(order.orderStatus) ? <CheckCircleOutlined /> : <ClockCircleOutlined />,
                    children: (
                      <div>
                        <Text strong={['DELIVERED', 'COMPLETED', 'PARTIAL_DELIVERED'].includes(order.orderStatus)} style={{ fontSize: 13 }}>Delivered</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{(order.shipments || [])[0]?.deliveredAt ? moment((order.shipments || [])[0].deliveredAt).format('DD MMM YYYY, HH:mm') : ['DELIVERED', 'COMPLETED'].includes(order.orderStatus) ? 'Completed' : '—'}</Text></div>
                      </div>
                    ),
                  },
                ]}
              />
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                <Text strong style={{ fontSize: 12, color: '#8c8c8c' }}>Current Status</Text>
                <div style={{ marginTop: 4 }}>
                  <Tag style={{ padding: '4px 10px', fontSize: 12 }} icon={statusConfig.icon}>
                    {statusConfig.text}
                  </Tag>
                  {(order.shipments || [])[0]?.shipmentStatus && (
                    <Tag color="blue" style={{ marginLeft: 6, fontSize: 11 }}>Shipment: {getStatusConfig((order.shipments || [])[0].shipmentStatus).text}</Tag>
                  )}
                </div>
              </div>
            </Card>

            {((order.shipments || [])[0]?.carrierName || (order.shipments || [])[0]?.trackingNumber || (order.shipments || [])[0]?.platformTrackingUrl || order.carrierName || order.trackingNumber || order.trackingUrl) && (
              <Card className={styles.sectionCard} style={{ marginTop: 12 }} title={<Space><SendOutlined className={styles.sectionIcon} /><span>Delivery Tracking</span></Space>}>
                {((order.shipments || [])[0] || order).carrierName && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Carrier</span>
                    <span className={styles.detailValue}>{(order.shipments || [])[0]?.carrierName || order.carrierName}</span>
                  </div>
                )}
                {((order.shipments || [])[0]?.trackingNumber || order.trackingNumber) && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Tracking #</span>
                    <span className={styles.detailValue}>{(order.shipments || [])[0]?.trackingNumber || order.trackingNumber}</span>
                  </div>
                )}
                {((order.shipments || [])[0]?.platformTrackingUrl || order.trackingUrl) && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Track</span>
                    <span className={styles.detailValue}>
                      <Button type="link" size="small" icon={<LinkOutlined />} href={(order.shipments || [])[0]?.platformTrackingUrl || order.trackingUrl} target="_blank" rel="noopener noreferrer">
                        Open tracking link
                      </Button>
                    </span>
                  </div>
                )}
              </Card>
            )}
          </Col>

          {/* Left Column - Main details */}
          <Col xs={{ span: 24, order: 2 }} lg={{ span: 14, order: 1 }} className={styles.leftColumn}>
            <Card
              className={`${styles.sectionCard} ${styles.customerCard}`}
              title={<Space><UserOutlined className={styles.sectionIcon} /><span>Customer Details</span></Space>}
            >
              <div className={styles.customerInfoRow}>
                <div className={styles.customerInfoLeft}>
                  <div className={styles.customerHero}>
                    <Avatar size={48} icon={<UserOutlined />} className={styles.customerAvatar} />
                    <div className={styles.customerHeroContent}>
                      <div className={styles.customerName}>{order.customerName || 'N/A'}</div>
                      {order.paymentType && (
                        <Tag color={order.paymentType === 'COD' ? 'orange' : 'green'} style={{ fontSize: 10, marginTop: 4 }}>
                          {order.paymentType}
                        </Tag>
                      )}
                      <div className={styles.customerContactInline}>
                        {order.customerPhone && (
                          <Space size={6}>
                            <PhoneOutlined style={{ fontSize: 12, color: '#22c55e' }} />
                            <span>{order.customerPhone}</span>
                            <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} icon={<PhoneOutlined />} onClick={() => window.open(`tel:${order.customerPhone}`)}>Call</Button>
                          </Space>
                        )}
                        {order.customerPhone && order.customerEmail && <span className={styles.contactDivider}>·</span>}
                        {order.customerEmail && (
                          <Space size={6}>
                            <MailOutlined style={{ fontSize: 12, color: '#3b82f6' }} />
                            <span>{order.customerEmail}</span>
                          </Space>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {deliveryAddress && (
                  <div className={styles.customerAddress}>
                    <div className={styles.addressHeader}>
                      <EnvironmentOutlined className={styles.addressIcon} />
                      <span>Delivery Address</span>
                      {deliveryAddress.latitude && deliveryAddress.longitude && (
                        <Button type="link" size="small" icon={<EnvironmentOutlined />} onClick={() => openMap(deliveryAddress.latitude, deliveryAddress.longitude)} className={styles.mapLink}>
                          View on Map
                        </Button>
                      )}
                    </div>
                    <Text className={styles.addressText}>
                      {[deliveryAddress.line1, deliveryAddress.line2, deliveryAddress.area, deliveryAddress.landmark, deliveryAddress.building_name, deliveryAddress.city, deliveryAddress.state, deliveryAddress.zipcode, deliveryAddress.country].filter(Boolean).join(', ')}
                    </Text>
                  </div>
                )}
              </div>
              {(order.orderNote || order.customer_note || order.note || order.internal_note) && (
                <div className={styles.notesBlock}>
                  <div className={styles.notesBlockHeader}>
                    <FileTextOutlined className={styles.notesIcon} />
                    <span>Notes</span>
                  </div>
                  <div className={styles.notesBlockContent}>
                    {(order.orderNote || order.customer_note) && (
                      <div className={styles.notesItem}>
                        <span className={styles.notesItemLabel}>Customer</span>
                        <span className={styles.notesItemText}>{order.orderNote || order.customer_note}</span>
                      </div>
                    )}
                    {(order.note || order.internal_note) && (
                      <div className={styles.notesItem}>
                        <span className={styles.notesItemLabel}>Internal</span>
                        <span className={styles.notesItemText}>{order.note || order.internal_note}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {(order.cancelNote || order.RescheduleDate) && (
                <div className={styles.customerAlerts}>
                  {order.cancelNote && (
                    <div className={styles.alertItem}>
                      <span className={styles.alertLabel}>Cancel reason</span>
                      <Text type="danger">{order.cancelNote}</Text>
                    </div>
                  )}
                  {order.RescheduleDate && (
                    <div className={`${styles.alertItem} ${styles.alertReschedule}`}>
                      <span className={styles.alertLabel}>Rescheduled</span>
                      <Text>{moment(order.RescheduleDate).format('DD MMM YYYY')}{order.RescheduleNote ? ` — ${order.RescheduleNote}` : ''}</Text>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Row gutter={16} style={{ marginTop: 0 }}>
              <Col xs={24} md={12} style={{ marginBottom: 16 }}>
                <Card
                  className={styles.sectionCard}
                  title={<Space><ShoppingCartOutlined className={styles.sectionIcon} /><span>Package Information</span></Space>}
                  style={{ height: '100%' }}
                >
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: orderItems.length ? 12 : 0, fontSize: 11, color: '#595959' }}>
                    <span><span className={styles.detailLabel}>items</span> <Badge count={orderItems.length} showZero size="small" style={{ backgroundColor: '#3b82f6', fontSize: 10 }} /></span>
                    <span><span className={styles.detailLabel}>total</span> <Text strong style={{ color: '#3b82f6', fontSize: 12 }}>{order.currency_symbol || '₹'}{parseFloat(order.amount || 0).toFixed(2)}</Text></span>
                    <span><span className={styles.detailLabel}>platform fee</span> {order.currency_symbol || '₹'}{parseFloat(order.platFormFree || 0).toFixed(2)}</span>
                    <span><span className={styles.detailLabel}>currency</span> {order.currency_code || 'INR'}</span>
                  </div>
                  {orderItems.length > 0 && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      {orderItems.map((item, index) => {
                        const qty = item.quantity || 1;
                        const unitPrice = parseFloat(item.productPrice ?? item.Product_price ?? 0);
                        const lineTotal = parseFloat(item.Product_amount ?? item.productAmount ?? 0);
                        const sym = item.currency_symbol || order.currency_symbol || '₹';
                        return (
                          <div key={index} className={styles.itemRow}>
                            <div className={styles.itemThumb}>
                              {item.productImage || item.Product_image ? (
                                <img src={item.productImage || item.Product_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5e7eb' }}>
                                  <ShoppingCartOutlined style={{ color: '#9ca3af', fontSize: 14 }} />
                                </div>
                              )}
                            </div>
                            <div className={styles.itemInfo}>
                              <div className={styles.itemName}>{item.productName || 'N/A'}</div>
                              <div className={styles.itemMeta}>
                                <span>qty: {qty}</span>
                                <span> · </span>
                                <span>{sym}{unitPrice.toFixed(2)} each</span>
                                <span> · </span>
                                <span className={styles.itemLineTotal}>{sym}{lineTotal.toFixed(2)} total</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12} style={{ marginBottom: 16 }}>
                <Card
                  className={styles.sectionCard}
                  title={<Space><DollarOutlined className={styles.sectionIcon} /><span>Payment Information</span></Space>}
                  extra={
                    order.paymentType === 'COD' ? (
                      <Button size="small" icon={<EditOutlined />} onClick={() => {
                        paymentForm.setFieldsValue({
                          paymentStatus: order.paymentStatus || 'PENDING',
                          paymentReceive: order.paymentReceive || 'NO',
                        });
                        setPaymentModalVisible(true);
                      }}>
                        Update Payment
                      </Button>
                    ) : null
                  }
                  style={{ height: '100%' }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                    <span><span className={styles.detailLabel}>Type</span> <Tag color={order.paymentType === 'COD' ? 'orange' : 'green'}>{order.paymentType || 'N/A'}</Tag></span>
                    <span><span className={styles.detailLabel}>Status</span> <Tag color={order.paymentStatus === 'PAID' ? 'green' : order.paymentStatus === 'UNPAID' ? 'red' : 'orange'}>{order.paymentStatus || 'PENDING'}</Tag></span>
                    <span><span className={styles.detailLabel}>Amount</span> <Text strong>{order.currency_symbol || '₹'}{parseFloat(order.amount || 0).toFixed(2)}</Text></span>
                    <span><span className={styles.detailLabel}>Received</span> <Badge status={order.paymentReceive === 'YES' ? 'success' : 'default'} text={order.paymentReceive || 'NO'} /></span>
                  </div>
                </Card>
              </Col>
            </Row>

            <Modal
              title="Update Payment"
              open={paymentModalVisible}
              onCancel={() => { setPaymentModalVisible(false); paymentForm.resetFields(); }}
              onOk={() => paymentForm.submit()}
              okText="Update"
              destroyOnHidden
            >
              <Form form={paymentForm} layout="vertical" onFinish={handlePaymentUpdate}>
                <Form.Item name="paymentStatus" label="Payment Status">
                  <Select placeholder="Select status">
                    <Option value="PENDING">Pending</Option>
                    <Option value="PAID">Paid</Option>
                    <Option value="UNPAID">Unpaid</Option>
                    <Option value="PARTIALLY_PAID">Partially Paid</Option>
                    <Option value="REFUNDED">Refunded</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="paymentReceive" label="Payment Received (COD collected)">
                  <Select placeholder="Select">
                    <Option value="YES">Yes</Option>
                    <Option value="NO">No</Option>
                  </Select>
                </Form.Item>
              </Form>
            </Modal>

            {/* Shipments Section - Multiple shipments with driver/warehouse per shipment */}
            <Card
              className={styles.sectionCard}
              title={
                <Space>
                  <SendOutlined className={styles.sectionIcon} />
                  <span>Shipments</span>
                  <Badge count={(order?.shipments || []).length} showZero style={{ backgroundColor: '#3b82f6' }} />
                </Space>
              }
              extra={
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateShipmentModalVisible(true)}>
                  Add Shipment
                </Button>
              }
            >
              {(order?.shipments || []).length === 0 ? (
                <div className={styles.shipmentEmpty}>
                  <SendOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 8 }} />
                  <Text type="secondary">No shipments yet</Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {assignmentMode === 'driver_accept' ? 'Create a shipment; a driver can accept from their app' : 'Create a shipment to assign warehouse and driver'}
                  </Text>
                  <Space style={{ marginTop: 12 }} wrap>
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateShipmentModalVisible(true)}>
                      Create Shipment
                    </Button>
                    <Button size="small" icon={<ShopOutlined />} onClick={() => { setSelectedShipmentId(null); warehouseForm.setFieldsValue({}); setWarehouseModalVisible(true); }}>
                      Create with Warehouse
                    </Button>
                    <Button size="small" icon={<CarOutlined />} onClick={() => { setSelectedShipmentId(null); driverForm.setFieldsValue({}); setDriverModalVisible(true); }}>
                      {assignmentMode === 'driver_accept' ? 'Create shipment (driver can accept)' : 'Create with Driver'}
                    </Button>
                  </Space>
                </div>
              ) : (
                <div className={styles.shipmentList}>
                  {(order?.shipments || []).map((shipment, idx) => {
                    const wh = shipment.pickupWarehouse || warehouses.find(w => w.id === shipment.pickupWarehouseId);
                    const drv = shipment.driver || drivers.find(d => d.id == shipment.driverId);
                    return (
                      <Card key={shipment.id} size="small" className={styles.shipmentCard}>
                        <div className={styles.shipmentHeader}>
                          <Space>
                            <Text strong style={{ fontSize: 13 }}>{shipment.shipmentNumber || `Shipment #${idx + 1}`}</Text>
                            <Tag color="blue" style={{ fontSize: 11 }}>{getStatusConfig(shipment.shipmentStatus || 'PENDING').text}</Tag>
                          </Space>
                          <Space size={8}>
                            <Tooltip title="Chalan">
                              <Button
                                size="small"
                                icon={<PrinterOutlined />}
                                onClick={() => handlePrintChalan(shipment)}
                              />
                            </Tooltip>
                            <Tooltip title="Update Status">
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  setSelectedShipmentForStatus(shipment);
                                  setShipmentStatusModalVisible(true);
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={wh ? 'Change Warehouse' : 'Assign Warehouse'}>
                              <Button
                                size="small"
                                icon={<ShopOutlined />}
                                onClick={() => {
                                  setSelectedShipmentId(shipment.id);
                                  warehouseForm.setFieldsValue({ warehouseId: shipment.pickupWarehouseId || wh?.id });
                                  setWarehouseModalVisible(true);
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={drv ? 'Change Driver' : 'Assign Driver'}>
                              <Button
                                size="small"
                                icon={<CarOutlined />}
                                onClick={() => {
                                  setSelectedShipmentId(shipment.id);
                                  driverForm.setFieldsValue({ driverId: shipment.driverId || drv?.id });
                                  setDriverModalVisible(true);
                                }}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Delete shipment"
                              description="Are you sure? This will remove the shipment and recalculate order status."
                              onConfirm={() => handleDeleteShipment(shipment.id)}
                              okText="Delete"
                              okButtonProps={{ danger: true }}
                              cancelText="Cancel"
                            >
                              <Tooltip title="Delete">
                                <span>
                                  <Button size="small" danger icon={<DeleteOutlined />} />
                                </span>
                              </Tooltip>
                            </Popconfirm>
                          </Space>
                        </div>
                        {((shipment.shipmentItems || []).length > 0 || orderItems.length > 0) && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                            <span className={styles.detailLabel} style={{ display: 'block', marginBottom: 6 }}>Products</span>
                            <div style={{ fontSize: 12, color: '#595959' }}>
                              {(shipment.shipmentItems || []).length > 0
                                ? (shipment.shipmentItems || []).map((si, i) => {
                                    const oi = si.orderItem || si.OrderItem;
                                    const name = oi?.productName || oi?.Product_name || 'N/A';
                                    const qty = si.quantity ?? oi?.quantity ?? 1;
                                    return (
                                      <span key={si.id || i}>
                                        {i > 0 && ' · '}
                                        {name} ({qty})
                                      </span>
                                    );
                                  })
                                : orderItems.map((item, i) => (
                                    <span key={item.id || i}>
                                      {i > 0 && ' · '}
                                      {item.productName || item.Product_name || 'N/A'} ({item.quantity || 1})
                                    </span>
                                  ))}
                            </div>
                          </div>
                        )}
                        <Row gutter={16} style={{ marginTop: 12 }}>
                          <Col span={12}>
                            <div className={styles.detailRow}>
                              <span className={styles.detailLabel}>Warehouse</span>
                              <span className={styles.detailValue}>
                                {wh ? (
                                  <Space direction="vertical" size={0}>
                                    <Text style={{ fontSize: 12 }}>{wh.name}</Text>
                                    {wh.address && <Text type="secondary" style={{ fontSize: 11 }}>{wh.address}</Text>}
                                  </Space>
                                ) : (
                                  <span className={styles.placeholder}>Not assigned</span>
                                )}
                              </span>
                            </div>
                          </Col>
                          <Col span={12}>
                            <div className={styles.detailRow}>
                              <span className={styles.detailLabel}>Driver</span>
                              <span className={styles.detailValue}>
                                {drv ? (
                                  <Space direction="vertical" size={0}>
                                    <Space>
                                      <Avatar size={24} icon={<UserOutlined />} style={{ backgroundColor: '#22c55e' }} />
                                      <Text style={{ fontSize: 12 }}>{drv.firstName} {drv.lastName}</Text>
                                    </Space>
                                    {drv.phone && <Text type="secondary" style={{ fontSize: 11 }}><PhoneOutlined /> {drv.phone}</Text>}
                                    {drv.vehicle_number && <Text type="secondary" style={{ fontSize: 11 }}>{drv.vehicle_number} ({drv.vehicleType})</Text>}
                                    <Badge status={drv.isAvailable === 'Online' ? 'success' : 'default'} text={drv.isAvailable || 'Offline'} />
                                  </Space>
                                ) : (
                                  <span className={styles.placeholder}>Not assigned</span>
                                )}
                              </span>
                            </div>
                          </Col>
                        </Row>
                        {(shipment.carrierName || shipment.trackingNumber || shipment.platformTrackingUrl) && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                            <div className={styles.detailRow}>
                              <span className={styles.detailLabel}>Tracking</span>
                              <span className={styles.detailValue}>
                                <Space>
                                  {shipment.carrierName && <Text style={{ fontSize: 12 }}>{shipment.carrierName}</Text>}
                                  {shipment.trackingNumber && <Tag>{shipment.trackingNumber}</Tag>}
                                  {shipment.platformTrackingUrl && (
                                    <Button type="link" size="small" icon={<LinkOutlined />} href={shipment.platformTrackingUrl} target="_blank" rel="noopener noreferrer">
                                      Track
                                    </Button>
                                  )}
                                </Space>
                              </span>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Modal
          title={selectedShipmentForStatus ? `Update Shipment Status - ${selectedShipmentForStatus.shipmentNumber || 'Shipment'}` : 'Update Shipment Status'}
          open={shipmentStatusModalVisible}
          onCancel={() => { setShipmentStatusModalVisible(false); setSelectedShipmentForStatus(null); shipmentStatusForm.resetFields(); }}
          onOk={() => shipmentStatusForm.submit()}
          okText="Update"
          destroyOnHidden
          styles={{ body: { paddingTop: 8 } }}
        >
          {selectedShipmentForStatus && (
            <Form
              key={selectedShipmentForStatus.id}
              form={shipmentStatusForm}
              layout="vertical"
              onFinish={handleShipmentStatusUpdate}
              initialValues={{ shipmentStatus: selectedShipmentForStatus.shipmentStatus || 'PENDING', note: '' }}
            >
              <Form.Item label="Current Status">
                <Tag color="blue">{getStatusConfig(selectedShipmentForStatus.shipmentStatus || 'PENDING').text}</Tag>
              </Form.Item>
              <Form.Item
                name="shipmentStatus"
                label="New Shipment Status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select placeholder="Select new status">
                  <Option value="PENDING">Pending</Option>
                  <Option value="PENDING_ACCEPTANCE">Waiting for Driver Accept</Option>
                  <Option value="READY_FOR_PICKUP">Ready for Pickup</Option>
                  <Option value="PICKED_UP">Picked Up</Option>
                  <Option value="IN_TRANSIT">In Transit</Option>
                  <Option value="OUT_FOR_DELIVERY">Out for Delivery</Option>
                  <Option value="ATTEMPTED_DELIVERY">Attempted Delivery</Option>
                  <Option value="DELIVERED">Delivered</Option>
                  <Option value="FAILURE">Failed</Option>
                  <Option value="RETURNED">Returned</Option>
                  <Option value="CANCELLED">Cancelled (Admin only)</Option>
                </Select>
              </Form.Item>
              <Form.Item name="note" label="Notes (Optional)">
                <TextArea rows={3} placeholder="Add any notes about this status change..." />
              </Form.Item>
            </Form>
          )}
        </Modal>

        {/* Create Shipment Modal */}
        <Modal
          title={<Space><PlusOutlined /><span>Create Shipment</span></Space>}
          open={createShipmentModalVisible}
          onCancel={() => {
            setCreateShipmentModalVisible(false);
            createShipmentForm.resetFields();
          }}
          onOk={() => createShipmentForm.submit()}
          okText="Create"
          width={480}
        >
          <Form
            form={createShipmentForm}
            layout="vertical"
            onFinish={handleCreateShipment}
          >
            {(orderItems || []).length > 0 && (() => {
              const shipped = getAvailableQtyByOrderItem();
              return (
                <Form.Item label="Products & Quantity">
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
                    {orderItems.map((oi, idx) => {
                      const orderQty = oi.quantity || 1;
                      const alreadyShipped = shipped[oi.id] || 0;
                      const availableQty = Math.max(0, orderQty - alreadyShipped);
                      return (
                        <div key={oi.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: idx < orderItems.length - 1 ? 10 : 0 }}>
                          <Form.Item name={['items', idx, 'orderItemId']} hidden><Input type="hidden" /></Form.Item>
                          <span style={{ flex: 1, fontSize: 13 }}>{oi.productName || oi.Product_name || 'N/A'}</span>
                          <Form.Item
                            name={['items', idx, 'quantity']}
                            rules={[{ required: true, message: 'Qty' }]}
                            style={{ marginBottom: 0, width: 80 }}
                          >
                            <InputNumber min={0} max={availableQty} size="small" style={{ width: '100%' }} placeholder="0" />
                          </Form.Item>
                          <Text type="secondary" style={{ fontSize: 11 }}>of {orderQty}{alreadyShipped > 0 ? ` (${availableQty} left)` : ''}</Text>
                        </div>
                      );
                    })}
                  </div>
                </Form.Item>
              );
            })()}
            <Form.Item name="warehouseId" label="Warehouse (optional)">
              <Select placeholder="Select warehouse" allowClear>
                {warehouses.map((wh) => (
                  <Option key={wh.id} value={wh.id}>{wh.name} - {wh.city}, {wh.state}</Option>
                ))}
              </Select>
            </Form.Item>
            {assignmentMode !== 'driver_accept' && (
              <Form.Item name="driverId" label="Driver (optional)">
                <Select placeholder="Select driver" allowClear>
                  {drivers.map((d) => (
                    <Option key={d.id} value={d.id}>{d.firstName} {d.lastName} - {d.vehicle_number}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {assignmentMode === 'driver_accept' && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                A driver can accept this shipment from their app after creation.
              </Text>
            )}
          </Form>
        </Modal>

        {/* Assign Warehouse Modal */}
        <Modal
          title={
            <Space>
              <ShopOutlined />
              <span>{selectedShipmentId ? 'Assign Warehouse to Shipment' : 'Create Shipment with Warehouse'}</span>
            </Space>
          }
          open={warehouseModalVisible}
          onCancel={() => {
            setWarehouseModalVisible(false);
            setSelectedShipmentId(null);
            warehouseForm.resetFields();
          }}
          onOk={() => warehouseForm.submit()}
          okText="Assign"
        >
          <Form
            form={warehouseForm}
            layout="vertical"
            onFinish={handleAssignWarehouse}
          >
            <Form.Item
              name="warehouseId"
              label="Select Warehouse"
              rules={[{ required: true, message: 'Please select a warehouse' }]}
            >
              <Select
                placeholder="Select warehouse"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {warehouses.map((wh) => (
                  <Option key={wh.id} value={wh.id}>
                    {wh.name} - {wh.city}, {wh.state}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.warehouseId !== curr.warehouseId}>
              {({ getFieldValue }) => {
                const whId = getFieldValue('warehouseId');
                const selectedWarehouse = whId ? warehouses.find(w => w.id === whId) : null;
                return selectedWarehouse ? (
              <Card size="small" className={styles.previewCard}>
                    <Space direction="vertical" size={4}>
                      <Text strong>{selectedWarehouse.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedWarehouse.address}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedWarehouse.city}, {selectedWarehouse.state} {selectedWarehouse.pincode}
                      </Text>
                      {selectedWarehouse.contactPhone && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <PhoneOutlined /> {selectedWarehouse.contactPhone}
                        </Text>
                      )}
                    </Space>
              </Card>
                ) : null;
              }}
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={
            <Space>
              <CarOutlined />
              <span>{selectedShipmentId ? 'Assign Driver to Shipment' : assignmentMode === 'driver_accept' ? 'Create Shipment' : 'Create Shipment with Driver'}</span>
            </Space>
          }
          open={driverModalVisible}
          onCancel={() => {
            setDriverModalVisible(false);
            setSelectedShipmentId(null);
            driverForm.resetFields();
          }}
          onOk={() => driverForm.submit()}
          okText={selectedShipmentId ? 'Assign' : 'Create'}
          width={600}
        >
          <Form
            form={driverForm}
            layout="vertical"
            onFinish={handleAssignDriver}
            initialValues={{ driverId: selectedShipmentId ? (order?.shipments || []).find(s => s.id === selectedShipmentId)?.driverId : (order?.shipments || [])[0]?.driverId }}
          >
            {assignmentMode === 'driver_accept' && !selectedShipmentId && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
                Leave driver empty for a driver to accept from their app, or select a driver to assign now.
              </Text>
            )}
            <Form.Item
              name="driverId"
              label={assignmentMode === 'driver_accept' && !selectedShipmentId ? 'Driver (optional)' : 'Select Driver'}
              rules={assignmentMode === 'driver_accept' && !selectedShipmentId ? [] : [{ required: true, message: 'Please select a driver' }]}
            >
              <Select
                placeholder="Search and select a driver"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {drivers.map((driverItem) => (
                  <Option key={driverItem.id} value={driverItem.id}>
                    {driverItem.firstName} {driverItem.lastName} - {driverItem.vehicle_number} ({driverItem.vehicleType}) {driverItem.isAvailable === 'Online' ? '🟢' : '⚫'}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.driverId !== curr.driverId}>
              {({ getFieldValue }) => {
                const drvId = getFieldValue('driverId');
                const selectedDriver = drvId ? drivers.find(d => d.id === drvId) : null;
                return selectedDriver ? (
              <Card size="small" className={styles.previewCard}>
                    <Space direction="vertical" size={4}>
                      <Space>
                        {selectedDriver.photoData?.url ? (
                          <Avatar 
                            src={selectedDriver.photoData.url} 
                            size={40}
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <Avatar 
                            icon={<UserOutlined />} 
                            size={40}
                            style={{ backgroundColor: '#1890ff' }} 
                          />
                        )}
                        <Text strong style={{ fontSize: 14 }}>{selectedDriver.firstName} {selectedDriver.lastName}</Text>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <PhoneOutlined /> {selectedDriver.phone}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <MailOutlined /> {selectedDriver.email}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Vehicle: {selectedDriver.vehicle_number} ({selectedDriver.vehicleType})
                      </Text>
                      <Badge
                        status={selectedDriver.isAvailable === 'Online' ? 'success' : 'default'}
                        text={selectedDriver.isAvailable || 'Offline'}
                      />
                    </Space>
              </Card>
                ) : null;
              }}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default OrderDetails;
